import React, { useState } from "react";
import { signIn, signUp, resetPasswordForEmail } from "../lib/auth";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { capture } from "../lib/analytics";
import { readPendingIntent } from "../lib/pendingIntent";
import { saveSignupAttribution } from "../lib/signupFunnel";

// #2131 — Supabase Auth's own mailer (no custom SMTP configured — see
// supabase/config.toml's commented-out [auth.email.smtp] block) enforces a
// low daily email-send quota. Once exhausted, every subsequent signup-
// confirmation or password-recovery call fails server-side, e.g.
// `gomail: could not send email 1: 550 "You have reached your daily email
// sending quota."`. This silently locked out real users (confirmed via
// auth_logs) with zero UI handling before this fix.
//
// `code` alone can't detect this in general — GoTrue gives it the catch-all
// "unexpected_failure", shared with unrelated 500s — so detection is
// message-text based, matched against GoTrue's own known wrapper phrasing
// (not bare keywords, so e.g. "email already registered" or "storage quota
// exceeded" don't get swept in). One code IS unambiguous and safe to match
// alone: over_email_send_rate_limit, Supabase's own outbound-email throttle.
const EMAIL_SEND_RATE_LIMIT_CODE = "over_email_send_rate_limit";

function isEmailDeliveryFailure(raw?: string | null, code?: string | null): boolean {
  if (code === EMAIL_SEND_RATE_LIMIT_CODE) return true;
  const m = (raw || "").trim().toLowerCase();
  if (!m) return false;
  if (m.includes("error sending") && m.includes("email")) return true; // "Error sending recovery/confirmation email"
  if (m.includes("email rate limit exceeded")) return true;
  if (m.includes("could not send email")) return true; // raw gomail prefix, if it ever reaches the client unwrapped
  if (m.includes("email") && m.includes("quota")) return true; // "...daily email sending quota..."
  return false;
}

// #2131 — the honest fallback for the failure class above. Says it's not
// the user's fault, gives a real next step, and deliberately makes no
// promise about timing — root cause could be a daily quota (resets
// tomorrow) or an open-ended SMTP/provider outage; this code can't know
// which. Exported (like friendlyAuthError itself) so tests can assert
// exact equality without duplicating the copy.
export const EMAIL_DELIVERY_FAILURE_MESSAGE =
  "We couldn't send that email — it's an issue on our end, not your account. " +
  "Please try again later, or email support@parcelyapp.com for help.";

// #1334 — Supabase surfaces the raw "Invalid login credentials" string on a
// failed password sign-in. That copy is terse and technical relative to the
// rest of the app. Map the small handful of known raw strings to the softer
// copy the issue's tone note asks for; anything else falls through unchanged
// so a genuinely unusual error message still reaches the user verbatim. Kept
// as a top-level pure function (not memoized, not a hook) — the mapping is
// static and the fn is cheap enough to run once per failed submit.
//
// `code` is optional and only used for the #2131 email-delivery-failure
// check above — existing callers/tests that only pass `raw` keep working
// unchanged.
export function friendlyAuthError(raw?: string | null, code?: string | null): string {
  if (isEmailDeliveryFailure(raw, code)) return EMAIL_DELIVERY_FAILURE_MESSAGE;
  const m = (raw || "").trim();
  if (!m) return "Sign-in failed. Please try again.";
  const lower = m.toLowerCase();
  if (lower === "invalid login credentials") {
    return "Incorrect email or password.";
  }
  return m;
}

interface AuthModalProps {
  onClose: () => void;
  /** Which tab to open on. The hard-wall "Get started" CTA opens "signup";
   *  "Sign in" / ?auth=login opens "signin" (the default). */
  initialMode?: "signin" | "signup";
}

const AuthModal: React.FC<AuthModalProps> = ({ onClose, initialMode }) => {
  const [mode, setMode] = useState<"signin" | "signup" | "reset">(initialMode ?? "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Set once a confirmation or reset email has been sent. The modal then shows a
  // "check your email" view instead of the form, so the user is not left looking
  // at the filled-in email and password fields wondering whether it worked.
  const [sent, setSent] = useState<{ kind: "signup" | "reset"; email: string } | null>(null);

  // Switch mode + clear transient state so a stale error/message never carries
  // across the tab/link transitions.
  const switchMode = (next: "signin" | "signup" | "reset") => {
    setMode(next);
    setError(null);
    setSent(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mode === "signup" && password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "reset") {
        const { error } = await resetPasswordForEmail(email);
        if (error) throw error;
        setSent({ kind: "reset", email });
      } else if (mode === "signup") {
        const { data, error } = await signUp(email, password);
        if (error) throw error;
        // Supabase anti-enumeration: signing up with an email that already
        // has a confirmed account returns NO error but an empty `identities`
        // array, and sends no confirmation email. Without this check the user
        // sees "check your email" for a mail that never arrives, and we fire
        // a false signup funnel event. Detect it and route them to sign-in.
        if (data?.user?.identities && data.user.identities.length === 0) {
          setError("That email already has an account. Try signing in, or reset your password.");
          return;
        }
        // #133 funnel event #1. Fires when the signup form succeeds (email
        // confirmation still pending) — the top of the activation funnel.
        //
        // This used to send county_selected = localStorage "parcely.countyId",
        // which is the DEFAULT county (or whatever tab was last open) for
        // nearly every new visitor — a county they never chose. Only an
        // explicit landing-page county is reported now; real picks arrive
        // later as county_picked. The anonymous id this event carries cannot
        // be joined to the user (cookieless analytics), so the same
        // attribution is saved for signup_completed, which fires after
        // identify in whichever tab the confirmation link opens.
        const params = new URLSearchParams(window.location.search);
        const pending = readPendingIntent();
        const source = params.get("utm_source") || (document.referrer ? "referral" : "direct");
        const landingCounty = pending?.countyId ?? null;
        capture("signup", { source, landing_county: landingCounty });
        saveSignupAttribution({ source, landingCounty, intent: pending?.intent?.kind ?? null });
        setSent({ kind: "signup", email });
        setPassword("");
        setConfirm("");
      } else {
        const { error } = await signIn(email, password);
        if (error) throw error;
        onClose();
      }
    } catch (err: any) {
      // #2131 — err?.code is threaded through so the email-delivery-failure
      // check in friendlyAuthError can match Supabase's unambiguous
      // over_email_send_rate_limit code even when the message text alone
      // wouldn't be enough to tell it apart from an unrelated error.
      setError(friendlyAuthError(err?.message, err?.code));
    } finally {
      setLoading(false);
    }
  };

  const submitLabel =
    mode === "reset" ? "Send reset link" : mode === "signin" ? "Sign in" : "Create account";

  return (
    <div className="auth-backdrop" onClick={onClose} role="presentation">
      <div
        className="auth-modal"
        onClick={(e) => e.stopPropagation()}
        ref={useFocusTrap<HTMLDivElement>({ onEscape: onClose })}
        role="dialog"
        aria-modal="true"
        aria-label="Sign in"
      >
        <button className="auth-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="auth-wordmark">
          Parcel<span>y</span>
        </div>

        {sent ? (
          <div className="auth-sent" role="status">
            <h2 className="auth-sent-title">
              {sent.kind === "signup" ? "Account created" : "Reset link sent"}
            </h2>
            <p className="auth-sent-body">
              {sent.kind === "signup"
                ? "Check your email to confirm your account, then sign in."
                : "Check your email for a password reset link."}
            </p>
            <p className="auth-sent-address">
              We sent it to <strong>{sent.email}</strong>.
            </p>
            <button type="button" className="auth-submit" onClick={() => switchMode("signin")}>
              Back to sign in
            </button>
            <p className="auth-note">Can't find it? Check your spam folder.</p>
          </div>
        ) : (
        <>
        {mode !== "reset" ? (
          <div className="auth-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "signin"}
              className={"auth-tab" + (mode === "signin" ? " auth-tab--active" : "")}
              onClick={() => switchMode("signin")}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "signup"}
              className={"auth-tab" + (mode === "signup" ? " auth-tab--active" : "")}
              onClick={() => switchMode("signup")}
            >
              Create account
            </button>
          </div>
        ) : (
          <div className="auth-reset-head">Reset your password</div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <label className="auth-field">
            <span className="auth-label">Email</span>
            <input
              className="auth-input mono"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@email.com"
            />
          </label>

          {mode !== "reset" && (
            // A <div> (not <label>) with an explicit htmlFor/id pairing:
            // <button> is itself a labelable element, so a wrapping <label>
            // would bind to the "Forgot password?" button instead of the
            // input and mis-name it for screen readers (#2131). "Forgot
            // password?" sits BELOW the input, right-aligned (#2148).
            <div className="auth-field">
              <label htmlFor="auth-password-input" className="auth-label">
                Password
              </label>
              <input
                id="auth-password-input"
                className="auth-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                placeholder="••••••••"
              />
              {mode === "signin" && (
                <button type="button" className="auth-forgot" onClick={() => switchMode("reset")}>
                  Forgot password?
                </button>
              )}
            </div>
          )}

          {mode === "signup" && (
            <label className="auth-field">
              <span className="auth-label">Confirm password</span>
              <input
                className="auth-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                placeholder="••••••••"
              />
            </label>
          )}

          {error && (
            <div className="auth-error">
              {error}
              {mode === "signin" && (
                <p className="auth-error-hint">
                  Signed up with a magic link?{" "}
                  <button type="button" className="auth-error-hint-link" onClick={() => switchMode("reset")}>
                    Set a password
                  </button>{" "}
                  first.
                </p>
              )}
            </div>
          )}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? "…" : submitLabel}
          </button>
        </form>

        {mode === "reset" ? (
          <button className="auth-toggle" onClick={() => switchMode("signin")}>
            Back to sign in
          </button>
        ) : (
          <p className="auth-note">Free to browse — no card required.</p>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
